import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  Tabs,
  Tab,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";

import {
  getEngagements,
  createEngagement,
  updateEngagement,
  deleteEngagement,
  getFournisseurs,
  getFormTemplates,
  getAvailableEngagements,
  submitEngagement,
  getEngagementSubmissions,
  updateEngagementSubmissionStatus,
  approveEngagementSubmissionForSupplier,
  sendEngagementSubmissionSupplierEmail,
} from "../api/api";

import DynamicForm from "../components/DynamicForm";
import AdherentShowcase from "../components/AdherentShowcase";
import { useNotifications } from "../components/NotificationsContext";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const isTruthy = (v) => v === true || v === "true" || v === 1 || v === "1";

function getUserFromLocalStorage() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function isAdminUser(user) {
  return Boolean(user?.is_amical_admin || user?.is_staff || user?.is_superuser);
}

function fournisseurToLabel(f) {
  if (!f) return "-";
  const name = f.name || f.nom;
  const login = f.login;
  if (name && login) return `${name} — ${login}`;
  if (name) return name;
  if (login) return login;
  return `#${f.id || "?"}`;
}

function getFournisseurId(x) {
  if (!x) return null;
  if (typeof x === "number") return x;
  if (typeof x === "string") return x;
  if (typeof x === "object") {
    return x.id ?? x.fournisseur_id ?? x.fournisseur ?? null;
  }
  return null;
}

function submissionStatusMeta(status) {
  switch (status) {
    case "submitted":
      return { label: "En cours", bg: "warning", textDark: true };
    case "processed":
      return { label: "Traité", bg: "success" };
    case "rejected":
      return { label: "Refusé", bg: "danger" };
    default:
      return { label: "Disponible", bg: "secondary" };
  }
}

function supplierStatusMeta(status) {
  switch (status) {
    case "pending":
      return { label: "Chez fournisseur", bg: "info" };
    case "approved":
      return { label: "Fournisseur OK", bg: "success" };
    case "rejected":
      return { label: "Fournisseur refuse", bg: "danger" };
    default:
      return { label: "Non envoye", bg: "secondary" };
  }
}

function mediaUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `http://localhost:8000${path}`;
}

function valueToText(value) {
  if (value === true) return "Oui";
  if (value === false) return "Non";
  if (value === null || value === undefined || value === "") return "-";
  if (Array.isArray(value)) return `${value.length} element(s)`;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function fullName(firstName, lastName, fallback = "-") {
  return `${firstName || ""} ${lastName || ""}`.trim() || fallback;
}

function personInfoText(person) {
  if (!person) return "";
  return [
    person.cin ? `CIN ${person.cin}` : null,
    person.matricule ? `Matricule ${person.matricule}` : null,
    person.telephone ? `Tel ${person.telephone}` : null,
    person.date_naissance ? `Ne(e) le ${person.date_naissance}` : null,
    person.age !== undefined && person.age !== null ? `${person.age} ans` : null,
    person.sexe ? `Sexe ${person.sexe}` : null,
    person.classe ? `Classe ${person.classe}` : null,
    person.etablissement || null,
    person.situation ? `Situation ${person.situation}` : null,
  ].filter(Boolean).join(" | ");
}

function resolveSubmissionValue(submission, key, value, field) {
  const normalizedKey = String(key || "").toLowerCase();
  const fieldType = String(field?.type || "").toLowerCase();
  const children = safeArray(submission?.adherent_enfants);
  const parents = safeArray(submission?.adherent_parents);

  if (fieldType === "adherent_child_select" || normalizedKey.includes("enfant")) {
    const child = children.find((item) => String(item.id) === String(value));
    if (child) {
      return {
        label: fullName(child.prenom, child.nom),
        details: personInfoText(child),
      };
    }
  }

  if (fieldType === "adherent_parent_select" || normalizedKey.includes("parent")) {
    const parent = parents.find((item) => String(item.id) === String(value));
    if (parent) {
      return {
        label: `${parent.type_parent || "Parent"}: ${fullName(parent.prenom, parent.nom)}`,
        details: personInfoText(parent),
      };
    }
  }

  return { label: valueToText(value), details: "" };
}

function orderedDataEntries(data) {
  return Object.entries(data || {})
    .filter(([key, value]) => {
      const isRemark = ["remarque", "remarques", "remark", "remarks"].includes(String(key).toLowerCase());
      if (!isRemark) return true;
      return !(value === null || value === undefined || value === "" || value === "-");
    })
    .sort(([a], [b]) => {
      const ar = ["remarque", "remarques", "remark", "remarks"].includes(String(a).toLowerCase());
      const br = ["remarque", "remarques", "remark", "remarks"].includes(String(b).toLowerCase());
      if (ar === br) return 0;
      return ar ? 1 : -1;
    });
}

function schemaFieldsByKey(schema) {
  const map = new Map();
  safeArray(schema?.fields).forEach((field) => map.set(field.key, field));
  return map;
}

function getSubmissionSchema(submission, engagements) {
  const engagement = engagements.find(
    (item) => String(item.id) === String(submission?.engagement)
  );
  return engagement?.form_template_schema || {};
}

function formatFieldLabel(key, schemaMap) {
  return schemaMap.get(key)?.label || key.replaceAll("_", " ");
}

function getSubmissionSummary(submission, schema) {
  const data = submission?.data || {};
  const schemaMap = schemaFieldsByKey(schema);
  return Object.entries(data)
    .filter(([, value]) => !Array.isArray(value))
    .slice(0, 3)
    .map(([key, value]) => `${formatFieldLabel(key, schemaMap)}: ${valueToText(value)}`)
    .join(" | ");
}

function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function htmlEscape(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function engagementArchiveStatus(item) {
  return item?.my_submission_status || "available";
}

export default function EngagementsList() {
  const { addNotification } = useNotifications();
  const location = useLocation();

  const user = useMemo(() => getUserFromLocalStorage(), []);
  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingSubmissionId, setUpdatingSubmissionId] = useState(null);
  const [approvingSubmissionId, setApprovingSubmissionId] = useState(null);
  const [sendingEmailSubmissionId, setSendingEmailSubmissionId] = useState(null);

  const [engagements, setEngagements] = useState([]);
  const [availableEngagements, setAvailableEngagements] = useState([]);
  const [engagementSubmissions, setEngagementSubmissions] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [createImagePreview, setCreateImagePreview] = useState(null);

  const [createForm, setCreateForm] = useState({
    title: "",
    image: null,
    fournisseur: "",
    form_template: "",
    is_active: true,
  });

  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminTab, setAdminTab] = useState(
    () => new URLSearchParams(location.search).get("tab") || "engagements"
  );

  const fournisseurLabelMap = useMemo(() => {
    const m = new Map();
    fournisseurs.forEach((f) => m.set(String(f.id), fournisseurToLabel(f)));
    return m;
  }, [fournisseurs]);

  const templatesFiltered = useMemo(() => {
    const fid = createForm.fournisseur;
    if (!fid) return templates;

    return templates.filter((t) => {
      const tplFid = getFournisseurId(t?.fournisseur) ?? t?.fournisseur_id ?? t?.fournisseur;
      return String(tplFid) === String(fid);
    });
  }, [templates, createForm.fournisseur]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [engRes, frRes, tplRes, subRes] = await Promise.all([
        getEngagements(),
        getFournisseurs(),
        getFormTemplates(),
        getEngagementSubmissions(),
      ]);

      setEngagements(safeArray(engRes.data));
      setFournisseurs(safeArray(frRes.data));
      setTemplates(safeArray(tplRes.data));
      setEngagementSubmissions(safeArray(subRes.data));
    } catch (err) {
      console.error("Erreur chargement admin:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger les données admin des engagements.",
        delay: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAdherentData = async () => {
    setLoading(true);
    try {
      const res = await getAvailableEngagements();
      setAvailableEngagements(safeArray(res.data));
    } catch (err) {
      console.error("Erreur chargement adhérent:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Impossible de charger les engagements disponibles.",
        delay: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAdminData();
    } else {
      fetchAdherentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) setAdminTab(tab);
  }, [location.search]);

  useEffect(() => {
    if (!isAdmin || engagementSubmissions.length === 0) return;
    const submissionId = new URLSearchParams(location.search).get("submission");
    if (!submissionId) return;
    const found = engagementSubmissions.find((item) => String(item.id) === String(submissionId));
    if (found) {
      setAdminTab("submissions");
      setSelectedSubmission(found);
    }
  }, [isAdmin, engagementSubmissions, location.search]);

  const openCreate = () => {
    setEditingEngagement(null);
    setErrors({});
    setCreateForm({
      title: "",
      image: null,
      fournisseur: "",
      form_template: "",
      is_active: true,
    });
    setCreateImagePreview(null);
    setShowCreate(true);
  };

  const openEditEngagement = (engagement) => {
    setEditingEngagement(engagement);
    setErrors({});
    setCreateForm({
      title: engagement.title || "",
      image: null,
      fournisseur: String(engagement.fournisseur || ""),
      form_template: String(engagement.form_template || ""),
      is_active: Boolean(engagement.is_active),
    });
    setCreateImagePreview(mediaUrl(engagement.image));
    setShowCreate(true);
  };

  const handleCreateImageChange = (file) => {
    setCreateForm((p) => ({ ...p, image: file || null }));
    if (!file) {
      setCreateImagePreview(editingEngagement ? mediaUrl(editingEngagement.image) : null);
      return;
    }
    setCreateImagePreview(URL.createObjectURL(file));
  };

  const validateCreate = () => {
    const e = {};
    if (!createForm.title?.trim()) e.title = "Titre obligatoire";
    if (!createForm.fournisseur) e.fournisseur = "Fournisseur obligatoire";
    if (!createForm.form_template) e.form_template = "Form template obligatoire";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreate()) return;

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("title", createForm.title.trim());
      payload.append("fournisseur", String(parseInt(createForm.fournisseur, 10)));
      payload.append("form_template", String(parseInt(createForm.form_template, 10)));
      payload.append("is_active", Boolean(createForm.is_active) ? "true" : "false");

      if (createForm.image) {
        payload.append("image", createForm.image);
      }

      const res = editingEngagement?.id
        ? await updateEngagement(editingEngagement.id, payload)
        : await createEngagement(payload);

      addNotification({
        type: "success",
        title: "Succès",
        message: editingEngagement?.id
          ? `Engagement modifié : ${res?.data?.title || createForm.title}`
          : `Engagement créé : ${res?.data?.title || createForm.title}`,
        delay: 4000,
      });

      setShowCreate(false);
      setEditingEngagement(null);
      setCreateImagePreview(null);
      await fetchAdminData();
    } catch (err) {
      const data = err?.response?.data || {};
      console.error("Erreur création engagement:", data);
      setErrors(data);

      addNotification({
        type: "error",
        title: "Erreur",
        message:
          data?.detail ||
          data?.title?.[0] ||
          data?.fournisseur?.[0] ||
          data?.form_template?.[0] ||
          "Création engagement échouée.",
        delay: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Supprimer l'engagement "${row.title}" ?`)) return;

    try {
      await deleteEngagement(row.id);
      setEngagements((prev) => prev.filter((x) => x.id !== row.id));

      addNotification({
        type: "success",
        title: "Suppression",
        message: "Engagement supprimé.",
        delay: 3500,
      });
    } catch (err) {
      console.error("Erreur suppression engagement:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message: "Suppression échouée.",
        delay: 7000,
      });
    }
  };

  const openEngagement = (engagement) => {
    setSelectedEngagement(engagement);
    setSubmitError(null);
    setShowEngagementModal(true);
  };

  const closeEngagement = () => {
    setSelectedEngagement(null);
    setSubmitError(null);
    setShowEngagementModal(false);
  };

  const handleSubmitEngagement = async (values, files) => {
    if (!selectedEngagement?.id) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await submitEngagement(selectedEngagement.id, values, files);

      addNotification({
        type: "success",
        title: "Succès",
        message: "Votre engagement a été envoyé avec succès.",
        delay: 5000,
      });

      closeEngagement();
      await fetchAdherentData();
    } catch (err) {
      const data = err?.response?.data || {};
      console.error("Erreur soumission engagement:", data);
      setSubmitError(data);

      addNotification({
        type: "error",
        title: "Erreur",
        message:
          data?.detail ||
          "Impossible d’envoyer le formulaire. Vérifiez les champs.",
        delay: 7000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSubmissionStatus = async (submissionId, nextStatus) => {
    setUpdatingSubmissionId(submissionId);
    try {
      await updateEngagementSubmissionStatus(submissionId, nextStatus);

      addNotification({
        type: "success",
        title: "Statut mis à jour",
        message: "Le statut de la soumission a bien été modifié.",
        delay: 4000,
      });

      await fetchAdminData();
    } catch (err) {
      console.error("Erreur update submission status:", err?.response?.data || err);
      addNotification({
        type: "error",
        title: "Erreur",
        message:
          err?.response?.data?.detail ||
          "Impossible de modifier le statut de la soumission.",
        delay: 7000,
      });
    } finally {
      setUpdatingSubmissionId(null);
    }
  };

  const handleApproveForSupplier = async (submissionId) => {
    setApprovingSubmissionId(submissionId);
    try {
      await approveEngagementSubmissionForSupplier(submissionId);
      addNotification({
        type: "success",
        title: "Envoye au fournisseur",
        message: "La demande est maintenant visible dans le dashboard fournisseur.",
        delay: 4000,
      });
      await fetchAdminData();
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur",
        message:
          err?.response?.data?.detail ||
          "Impossible d'envoyer la demande au fournisseur.",
        delay: 7000,
      });
    } finally {
      setApprovingSubmissionId(null);
    }
  };

  const handleSendSupplierEmail = async (submission) => {
    if (!submission?.id) return;
    const engagement = engagements.find((item) => String(item.id) === String(submission.engagement));
    const defaultEmail = engagement?.fournisseur_login || "";
    const email = window.prompt("Email fournisseur", defaultEmail);
    if (!email) return;

    setSendingEmailSubmissionId(submission.id);
    try {
      await sendEngagementSubmissionSupplierEmail(submission.id, email.trim());
      addNotification({
        type: "success",
        title: "Email envoye",
        message: "Le PDF a ete envoye au fournisseur. Le traitement reste disponible.",
        delay: 5000,
      });
      await fetchAdminData();
    } catch (err) {
      addNotification({
        type: "error",
        title: "Erreur email",
        message:
          err?.response?.data?.email ||
          err?.response?.data?.detail ||
          "Impossible d'envoyer l'email au fournisseur.",
        delay: 8000,
      });
    } finally {
      setSendingEmailSubmissionId(null);
    }
  };

  const exportSubmissionDetailPdf = (submission) => {
    if (!submission) return;
    const schema = getSubmissionSchema(submission, engagements);
    const schemaMap = schemaFieldsByKey(schema);
    const dataRows = orderedDataEntries(submission.data || {}).map(([key, value]) => {
      const label = htmlEscape(formatFieldLabel(key, schemaMap));
      const rendered = Array.isArray(value)
        ? value.map((item, index) => {
            const lines = Object.entries(item || {})
              .map(([childKey, childValue]) => `${htmlEscape(childKey)}: ${htmlEscape(valueToText(childValue))}`)
              .join("<br/>");
            return `<strong>Ligne ${index + 1}</strong><br/>${lines}`;
          }).join("<hr/>")
        : (() => {
            const resolved = resolveSubmissionValue(submission, key, value, schemaMap.get(key));
            return `${htmlEscape(resolved.label)}${resolved.details ? `<br/><small>${htmlEscape(resolved.details)}</small>` : ""}`;
          })();
      return `<tr><th>${label}</th><td>${rendered}</td></tr>`;
    }).join("");

    const fileRows = safeArray(submission.files).map((file) => `
      <tr>
        <th>${htmlEscape(formatFieldLabel(file.field_key, schemaMap))}</th>
        <td>${htmlEscape(mediaUrl(file.file) || "-")}</td>
      </tr>
    `).join("");

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>Demande engagement #${htmlEscape(submission.id)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #202124; }
            .header { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #1f7a2f; padding-bottom: 12px; margin-bottom: 20px; }
            .header img { width: 58px; height: 58px; object-fit: contain; }
            .brand { font-weight: 800; color: #1f7a2f; letter-spacing: .08em; text-transform: uppercase; font-size: 12px; }
            h1 { font-size: 22px; margin: 0 0 6px; }
            .muted { color: #5f6368; margin-bottom: 22px; }
            table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 13px; }
            th, td { border: 1px solid #d7dce1; padding: 9px; text-align: left; vertical-align: top; }
            th { width: 34%; background: #f6f8fa; }
            .section { margin-top: 22px; font-weight: 700; }
            .signature { margin-top: 34px; display: flex; justify-content: flex-end; }
            .signature img { width: 220px; max-height: 120px; object-fit: contain; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="/logoamicale.png" alt="Amicale" />
            <div>
              <div class="brand">Amicale Autoroutes</div>
              <strong>Fiche detaillee de demande</strong>
            </div>
          </div>
          <h1>Demande engagement #${htmlEscape(submission.id)}</h1>
          <div class="muted">
            ${htmlEscape(submission.engagement_title || "-")} -
            ${htmlEscape(`${submission.adherent_first_name || ""} ${submission.adherent_last_name || ""}`.trim() || "-")}
            (${htmlEscape(submission.adherent_matricule || "-")}) -
            CIN ${htmlEscape(submission.adherent_cin || "-")}
          </div>
          <table>
            <tr><th>Adherent</th><td>${htmlEscape(fullName(submission.adherent_first_name, submission.adherent_last_name))}</td></tr>
            <tr><th>Matricule</th><td>${htmlEscape(submission.adherent_matricule || "-")}</td></tr>
            <tr><th>CIN</th><td>${htmlEscape(submission.adherent_cin || "-")}</td></tr>
            <tr><th>Telephone</th><td>${htmlEscape(submission.adherent_telephone || "-")}</td></tr>
            <tr><th>Statut admin</th><td>${htmlEscape(submissionStatusMeta(submission.status).label)}</td></tr>
            <tr><th>Statut fournisseur</th><td>${htmlEscape(supplierStatusMeta(submission.supplier_status).label)}</td></tr>
            <tr><th>Cause fournisseur</th><td>${htmlEscape(submission.supplier_refusal_reason || "-")}</td></tr>
            <tr><th>Email fournisseur</th><td>${htmlEscape(submission.supplier_email_sent_at ? `${submission.supplier_email_to || "-"} - ${new Date(submission.supplier_email_sent_at).toLocaleString()}` : "-")}</td></tr>
            <tr><th>Date demande</th><td>${htmlEscape(submission.created_at ? new Date(submission.created_at).toLocaleString() : "-")}</td></tr>
          </table>
          <div class="section">Reponses</div>
          <table>${dataRows || "<tr><td>Aucune reponse</td></tr>"}</table>
          ${fileRows ? `<div class="section">Pieces jointes</div><table>${fileRows}</table>` : ""}
          <div class="signature"><img src="/signature-amicale.jpg" onerror="this.onerror=null;this.src='/signateur-amicale.jpg'" alt="Signature Amicale" /></div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const exportSubmissionsExcel = () => {
    const rows = [
      ["ID", "Engagement", "Adherent", "Matricule", "CIN", "Statut", "Date", "Resume"],
      ...engagementSubmissions.map((sub) => {
        const schema = getSubmissionSchema(sub, engagements);
        return [
          sub.id,
          sub.engagement_title || "",
          `${sub.adherent_first_name || ""} ${sub.adherent_last_name || ""}`.trim(),
          sub.adherent_matricule || "",
          sub.adherent_cin || "",
          submissionStatusMeta(sub.status).label,
          sub.created_at ? new Date(sub.created_at).toLocaleString() : "",
          getSubmissionSummary(sub, schema),
        ];
      }),
    ];

    const csv = rows.map((row) => row.map(csvCell).join(";")).join("\n");
    downloadTextFile("demandes-engagements.csv", csv, "text/csv;charset=utf-8");
  };

  const exportSubmissionsPdf = () => {
    const htmlRows = engagementSubmissions.map((sub) => {
      const schema = getSubmissionSchema(sub, engagements);
      return `
        <tr>
          <td>${sub.id}</td>
          <td>${sub.engagement_title || ""}</td>
          <td>${sub.adherent_first_name || ""} ${sub.adherent_last_name || ""}</td>
          <td>${sub.adherent_matricule || ""}</td>
          <td>${sub.adherent_cin || ""}</td>
          <td>${submissionStatusMeta(sub.status).label}</td>
          <td>${sub.created_at ? new Date(sub.created_at).toLocaleString() : ""}</td>
          <td>${getSubmissionSummary(sub, schema)}</td>
        </tr>
      `;
    }).join("");

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <title>Demandes engagements</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            .header { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #1f7a2f; padding-bottom: 12px; margin-bottom: 20px; }
            .header img { width: 58px; height: 58px; object-fit: contain; }
            .brand { font-weight: 800; color: #1f7a2f; letter-spacing: .08em; text-transform: uppercase; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
            th { background: #f2f2f2; }
            .signature { margin-top: 34px; display: flex; justify-content: flex-end; }
            .signature img { width: 220px; max-height: 120px; object-fit: contain; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="/logoamicale.png" alt="Amicale" />
            <div>
              <div class="brand">Amicale Autoroutes</div>
              <strong>Etat des demandes engagements</strong>
            </div>
          </div>
          <h2>Demandes engagements</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Engagement</th><th>Adherent</th><th>Matricule</th><th>CIN</th>
                <th>Statut</th><th>Date</th><th>Resume</th>
              </tr>
            </thead>
            <tbody>${htmlRows}</tbody>
          </table>
          <div class="signature"><img src="/signature-amicale.jpg" onerror="this.onerror=null;this.src='/signateur-amicale.jpg'" alt="Signature Amicale" /></div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  const renderSubmissionDetails = (submission) => {
    if (!submission) return null;

    const schema = getSubmissionSchema(submission, engagements);
    const schemaMap = schemaFieldsByKey(schema);
    const data = submission.data || {};

    return (
      <div>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted" style={{ fontSize: 12 }}>Adherent</div>
                <div style={{ fontWeight: 700 }}>
                  {submission.adherent_first_name || "-"} {submission.adherent_last_name || ""}
                </div>
                <div className="text-muted">{submission.adherent_matricule || "-"}</div>
                <div className="text-muted">CIN {submission.adherent_cin || "-"}</div>
                <div className="text-muted">Tel {submission.adherent_telephone || "-"}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="h-100">
              <Card.Body>
                <div className="text-muted" style={{ fontSize: 12 }}>Engagement</div>
                <div style={{ fontWeight: 700 }}>{submission.engagement_title || "-"}</div>
                <Badge
                  bg={submissionStatusMeta(submission.status).bg}
                  text={submissionStatusMeta(submission.status).textDark ? "dark" : undefined}
                >
                  {submissionStatusMeta(submission.status).label}
                </Badge>
                <div className="mt-2">
                  <Badge bg={supplierStatusMeta(submission.supplier_status).bg}>
                    {supplierStatusMeta(submission.supplier_status).label}
                  </Badge>
                </div>
                {submission.supplier_refusal_reason && (
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    Cause: {submission.supplier_refusal_reason}
                  </div>
                )}
                {submission.supplier_email_sent_at && (
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    Email envoye a {submission.supplier_email_to || "-"} le{" "}
                    {new Date(submission.supplier_email_sent_at).toLocaleString()}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-3">
          <Card.Body>
            <h6 className="mb-3">Reponses</h6>
            {Object.keys(data).length === 0 ? (
              <Alert variant="warning" className="mb-0">Aucune reponse trouvee.</Alert>
            ) : (
              orderedDataEntries(data).map(([key, value]) => {
                const field = schemaMap.get(key);

                if (Array.isArray(value)) {
                  const childFields = safeArray(field?.fields);

                  return (
                    <div key={key} className="mb-4">
                      <div style={{ fontWeight: 700 }} className="mb-2">
                        {formatFieldLabel(key, schemaMap)}
                      </div>

                      {value.length === 0 ? (
                        <div className="text-muted">Aucune ligne.</div>
                      ) : (
                        value.map((item, index) => (
                          <Card key={`${key}-${index}`} className="mb-2 bg-light border-0">
                            <Card.Body>
                              <div style={{ fontWeight: 700 }} className="mb-2">
                                Ligne {index + 1}
                              </div>
                              {Object.entries(item || {}).map(([childKey, childValue]) => {
                                const child = childFields.find((f) => f.key === childKey);
                                return (
                                  <div key={childKey} className="d-flex justify-content-between border-bottom py-1">
                                    <span className="text-muted">{child?.label || childKey}</span>
                                    <span style={{ fontWeight: 600 }}>{valueToText(childValue)}</span>
                                  </div>
                                );
                              })}
                            </Card.Body>
                          </Card>
                        ))
                      )}
                    </div>
                  );
                }

                const resolved = resolveSubmissionValue(submission, key, value, field);

                return (
                  <div key={key} className="d-flex justify-content-between border-bottom py-2">
                    <span className="text-muted">{formatFieldLabel(key, schemaMap)}</span>
                    <span style={{ fontWeight: 600, textAlign: "right" }}>
                      {resolved.label}
                      {resolved.details && (
                        <small className="d-block text-muted" style={{ fontWeight: 400 }}>
                          {resolved.details}
                        </small>
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </Card.Body>
        </Card>

        {safeArray(submission.files).length > 0 && (
          <Card>
            <Card.Body>
              <h6 className="mb-3">Pieces jointes</h6>
              {submission.files.map((file) => (
                <div key={file.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                  <span>{formatFieldLabel(file.field_key, schemaMap)}</span>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    href={mediaUrl(file.file)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ouvrir
                  </Button>
                </div>
              ))}
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  return (
    <Container className="py-4" style={{ maxWidth: 1180 }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Engagements</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            {isAdmin ? "Gestion admin des engagements" : "Liste des engagements disponibles"}
          </div>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={isAdmin ? fetchAdminData : fetchAdherentData}
          >
            🔄 Rafraîchir
          </Button>

          {isAdmin && (
            <>
              <Button
                variant="outline-success"
                onClick={exportSubmissionsExcel}
                disabled={engagementSubmissions.length === 0}
              >
                Export Excel
              </Button>
              <Button
                variant="outline-danger"
                onClick={exportSubmissionsPdf}
                disabled={engagementSubmissions.length === 0}
              >
                Export PDF
              </Button>
              <Button variant="success" onClick={openCreate}>
                Créer un engagement
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : isAdmin ? (
        <>
          <Card className="mb-3">
            <Card.Body>
              <div style={{ fontSize: 14 }}>
                <b>Important :</b> la page <code>Formulaires (templates)</code> sert seulement à créer
                les modèles de formulaire. La page <code>Engagements</code> sert à créer l’engagement,
                suivre les demandes et gérer les statuts.
              </div>
            </Card.Body>
          </Card>

          <Tabs
            activeKey={adminTab}
            onSelect={(k) => setAdminTab(k || "engagements")}
            className="mb-3"
          >
            <Tab eventKey="engagements" title="Engagements">
              {engagements.length === 0 ? (
                <Alert variant="warning">
                  Aucun engagement trouvé. Crée d’abord un engagement avec un fournisseur et un template.
                </Alert>
              ) : (
                <Card>
                  <Card.Body>
                    <Table bordered hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Image</th>
                          <th>Titre</th>
                          <th>Fournisseur</th>
                          <th>Form template</th>
                          <th>Actif</th>
                          <th style={{ width: 160 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {engagements.map((e) => {
                          const fid = String(getFournisseurId(e?.fournisseur) ?? e?.fournisseur ?? "");
                          const fournisseurLabel =
                            e?.fournisseur_name && e?.fournisseur_login
                              ? `${e.fournisseur_name} — ${e.fournisseur_login}`
                              : e?.fournisseur_login
                              ? e.fournisseur_login
                              : fournisseurLabelMap.get(fid) || fid || "-";

                          const templateLabel =
                            e?.form_template_name || `Template #${e?.form_template ?? "-"}`;

                          return (
                            <tr key={e.id}>
                              <td>{e.id}</td>
                              <td style={{ width: 92 }}>
                                {mediaUrl(e.image) ? (
                                  <img
                                    src={mediaUrl(e.image)}
                                    alt={e.title || "Engagement"}
                                    style={{
                                      width: 70,
                                      height: 48,
                                      objectFit: "cover",
                                      borderRadius: 8,
                                    }}
                                  />
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <div style={{ fontWeight: 700 }}>{e.title}</div>
                                <div className="text-muted" style={{ fontSize: 12 }}>
                                  slug: {e.slug || "-"}
                                </div>
                              </td>
                              <td>{fournisseurLabel}</td>
                              <td>{templateLabel}</td>
                              <td>
                                {isTruthy(e.is_active) ? (
                                  <Badge bg="success">Oui</Badge>
                                ) : (
                                  <Badge bg="secondary">Non</Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => openEditEngagement(e)}
                                  >
                                    Modifier
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(e)}
                                  >
                                    Supprimer
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            <Tab eventKey="submissions" title="Demandes adhérents">
              {engagementSubmissions.length === 0 ? (
                <Alert variant="warning">
                  Aucune demande d’engagement pour le moment.
                </Alert>
              ) : (
                <Card>
                  <Card.Body>
                    <Table bordered hover responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Engagement</th>
                          <th>Adhérent</th>
                          <th>Statut</th>
                          <th>Fournisseur</th>
                          <th>Date</th>
                          <th>Resume</th>
                          <th style={{ width: 280 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {engagementSubmissions.map((sub) => {
                          const meta = submissionStatusMeta(sub.status);
                          const supplierMeta = supplierStatusMeta(sub.supplier_status);
                          const schema = getSubmissionSchema(sub, engagements);
                          const summary = getSubmissionSummary(sub, schema);

                          return (
                            <tr key={sub.id}>
                              <td>{sub.id}</td>
                              <td>{sub.engagement_title || "-"}</td>
                              <td>
                                <div style={{ fontWeight: 600 }}>
                                  {sub.adherent_first_name || "-"} {sub.adherent_last_name || ""}
                                </div>
                                <div className="text-muted" style={{ fontSize: 12 }}>
                                  {sub.adherent_matricule || "-"}
                                </div>
                              </td>
                              <td>
                                <Badge bg={meta.bg} text={meta.textDark ? "dark" : undefined}>
                                  {meta.label}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={supplierMeta.bg}>
                                  {supplierMeta.label}
                                </Badge>
                                {sub.supplier_refusal_reason && (
                                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                    {sub.supplier_refusal_reason}
                                  </div>
                                )}
                                {sub.supplier_email_sent_at && (
                                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                    Email: {sub.supplier_email_to || "-"}
                                  </div>
                                )}
                              </td>
                              <td>
                                {sub.created_at
                                  ? new Date(sub.created_at).toLocaleString()
                                  : "-"}
                              </td>
                              <td style={{ minWidth: 260 }}>
                                <div style={{ fontSize: 13 }}>
                                  {summary || "Demande sans resume"}
                                </div>
                                {safeArray(sub.files).length > 0 && (
                                  <Badge bg="info" className="mt-2">
                                    {sub.files.length} piece(s) jointe(s)
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2 flex-wrap">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => setSelectedSubmission(sub)}
                                  >
                                    Details
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={() => exportSubmissionDetailPdf(sub)}
                                  >
                                    PDF
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    disabled={
                                      sendingEmailSubmissionId === sub.id ||
                                      sub.status !== "submitted"
                                    }
                                    onClick={() => handleSendSupplierEmail(sub)}
                                  >
                                    {sendingEmailSubmissionId === sub.id ? "..." : "Email PDF"}
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={
                                      approvingSubmissionId === sub.id ||
                                      sub.status !== "submitted" ||
                                      ["pending", "approved"].includes(sub.supplier_status)
                                    }
                                    onClick={() => handleApproveForSupplier(sub.id)}
                                  >
                                    {approvingSubmissionId === sub.id ? "..." : "Approuver"}
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={
                                      updatingSubmissionId === sub.id ||
                                      sub.status === "processed" ||
                                      (sub.supplier_status !== "approved" && !sub.supplier_email_sent_at)
                                    }
                                    onClick={() =>
                                      handleUpdateSubmissionStatus(sub.id, "processed")
                                    }
                                  >
                                    {updatingSubmissionId === sub.id ? "..." : "Traiter"}
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="danger"
                                    disabled={
                                      updatingSubmissionId === sub.id ||
                                      sub.status === "rejected"
                                    }
                                    onClick={() =>
                                      handleUpdateSubmissionStatus(sub.id, "rejected")
                                    }
                                  >
                                    {updatingSubmissionId === sub.id ? "..." : "Refuser"}
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={
                                      updatingSubmissionId === sub.id ||
                                      sub.status === "submitted"
                                    }
                                    onClick={() =>
                                      handleUpdateSubmissionStatus(sub.id, "submitted")
                                    }
                                  >
                                    Remettre en cours
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>
          </Tabs>

          <Modal
            show={!!selectedSubmission}
            onHide={() => setSelectedSubmission(null)}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Details de la demande</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {renderSubmissionDetails(selectedSubmission)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-info" onClick={() => exportSubmissionDetailPdf(selectedSubmission)}>
                Export PDF detail
              </Button>
              <Button variant="secondary" onClick={() => setSelectedSubmission(null)}>
                Fermer
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showCreate}
            onHide={() => {
              setShowCreate(false);
              setEditingEngagement(null);
              setCreateImagePreview(null);
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editingEngagement ? "Modifier un engagement" : "Créer un engagement"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Titre *</Form.Label>
                <Form.Control
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  isInvalid={!!errors.title}
                  placeholder="Ex: Demande Ooredoo - Mobile"
                />
                <Form.Control.Feedback type="invalid">
                  {Array.isArray(errors.title) ? errors.title[0] : errors.title}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Image de l'engagement</Form.Label>
                <label className="modern-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleCreateImageChange(e.target.files?.[0] || null)}
                  />
                  <div>
                    <strong>Televerser un fichier</strong>
                    <span>PNG, JPG ou WEBP. Apercu instantane apres selection.</span>
                  </div>
                  {createImagePreview ? (
                    <img src={createImagePreview} alt="Apercu engagement" />
                  ) : (
                    <em>Image</em>
                  )}
                </label>
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  Optionnel. Laisse vide pour garder l'image actuelle.
                </div>
              </Form.Group>

              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fournisseur *</Form.Label>
                    <Form.Select
                      value={createForm.fournisseur}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          fournisseur: e.target.value,
                          form_template: "",
                        }))
                      }
                      isInvalid={!!errors.fournisseur}
                    >
                      <option value="">-- choisir --</option>
                      {fournisseurs.map((f) => (
                        <option key={f.id} value={f.id}>
                          {fournisseurToLabel(f)} (id: {f.id})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {Array.isArray(errors.fournisseur)
                        ? errors.fournisseur[0]
                        : errors.fournisseur}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Form template *</Form.Label>
                    <Form.Select
                      value={createForm.form_template}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          form_template: e.target.value,
                        }))
                      }
                      isInvalid={!!errors.form_template}
                      disabled={!createForm.fournisseur}
                    >
                      <option value="">
                        {!createForm.fournisseur
                          ? "Choisir fournisseur d'abord"
                          : "-- choisir --"}
                      </option>

                      {templatesFiltered.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name || `Template #${t.id}`} (id: {t.id})
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Control.Feedback type="invalid">
                      {Array.isArray(errors.form_template)
                        ? errors.form_template[0]
                        : errors.form_template}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Check
                type="switch"
                label="Actif"
                checked={Boolean(createForm.is_active)}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, is_active: e.target.checked }))
                }
              />

              {errors.detail && (
                <Alert variant="danger" className="mt-3">
                  {errors.detail}
                </Alert>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreate(false);
                  setEditingEngagement(null);
                  setCreateImagePreview(null);
                }}
                disabled={saving}
              >
                Annuler
              </Button>
              <Button variant="primary" onClick={handleCreate} disabled={saving}>
                {saving ? <Spinner size="sm" className="me-2" /> : null}
                {editingEngagement ? "Enregistrer" : "Créer"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <>
          <AdherentShowcase
            title="Engagements"
            eyebrow="Catalogue"
            subtitle="Tous les engagements disponibles sont affiches ici avec recherche, statut et pagination."
            items={availableEngagements}
            searchPlaceholder="Rechercher par titre, fournisseur ou formulaire..."
            emptyText="Aucun engagement disponible pour le moment."
            showCarousel={false}
            archiveIncludesNewest
            archiveTitle="Tous les engagements"
            getDate={(e) => e.created_at || e.updated_at || e.id}
            getStatus={engagementArchiveStatus}
            getSearchText={(e) =>
              `${e.title || ""} ${e.fournisseur_login || ""} ${e.form_template_name || ""}`
            }
            statusOptions={[
              { value: "available", label: "Disponible" },
              { value: "submitted", label: "En cours" },
              { value: "processed", label: "Traite" },
              { value: "rejected", label: "Refuse" },
            ]}
            renderHero={(e) => {
              const meta = submissionStatusMeta(e.my_submission_status);

              return (
                <article className="showcase-hero">
                  <div className="showcase-hero-media">
                    {mediaUrl(e.image) ? (
                      <img src={mediaUrl(e.image)} alt={e.title || "Engagement"} />
                    ) : (
                      <div className="showcase-hero-media-placeholder">EG</div>
                    )}
                  </div>
                  <div className="showcase-hero-content">
                    <div>
                      <Badge bg={meta.bg} text={meta.textDark ? "dark" : undefined}>
                        {meta.label}
                      </Badge>
                    </div>
                    <h3>{e.title}</h3>
                    <p>
                      Engagement fournisseur avec formulaire simplifie pour envoyer votre demande.
                    </p>
                    <div className="showcase-meta">
                      <span className="showcase-pill">
                        Fournisseur: {e.fournisseur_login || e.fournisseur || "-"}
                      </span>
                      <span className="showcase-pill">
                        Formulaire: {e.form_template_name || "-"}
                      </span>
                    </div>
                    <div className="showcase-actions">
                      <Button
                        variant="primary"
                        onClick={() => openEngagement(e)}
                        disabled={e.my_submission_status === "processed"}
                      >
                        {e.my_submission_status ? "Voir / Mettre a jour" : "Ouvrir l'engagement"}
                      </Button>
                    </div>
                  </div>
                </article>
              );
            }}
            renderArchiveRow={(e) => {
              const meta = submissionStatusMeta(e.my_submission_status);

              return (
                <article className="showcase-row" key={e.id}>
                  {mediaUrl(e.image) ? (
                    <img src={mediaUrl(e.image)} alt={e.title || "Engagement"} />
                  ) : (
                    <div className="showcase-row-placeholder">EG</div>
                  )}
                  <div>
                    <h4 className="showcase-row-title">{e.title}</h4>
                    <p className="showcase-row-subtitle">
                      Fournisseur: {e.fournisseur_login || e.fournisseur || "-"} | Formulaire:{" "}
                      {e.form_template_name || "-"}
                    </p>
                  </div>
                  <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                    <Badge bg={meta.bg} text={meta.textDark ? "dark" : undefined}>
                      {meta.label}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openEngagement(e)}
                      disabled={e.my_submission_status === "processed"}
                    >
                      Ouvrir
                    </Button>
                  </div>
                </article>
              );
            }}
          />

          <Modal
            show={showEngagementModal}
            onHide={closeEngagement}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedEngagement?.title || "Engagement"}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {selectedEngagement?.form_template_schema ? (
                <DynamicForm
                  schema={selectedEngagement.form_template_schema}
                  onSubmit={handleSubmitEngagement}
                  loading={submitting}
                  error={submitError}
                />
              ) : (
                <Alert variant="warning">
                  Aucun formulaire disponible pour cet engagement.
                </Alert>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={closeEngagement}>
                Fermer
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </Container>
  );
}
